<template>
  <div>
    <vx-card v-if="board" class="w-full">
      <div class="flex justify-between items-center">
        <h4 class="mb-8">공정활동분류표 엑셀 업로드</h4>
        <vs-button color="secondary" @click="onClickSave">저장</vs-button>
      </div>

      <!-- 기본정보 -->
      <field-select-module v-model="stdInfo" :board="board" :types="['department', 'com_name', 'rank', 'writer_name', 'date']" />
      <div class="writeGridContainer">
        <div class="writeGridLineItem">
          <div>파일 업로드</div>
          <div class="flex flex-wrap gap-2">
            <vs-button class="w-full sm:w-auto" color="secondary" type="border" @click="onClickDownloadTemplate">엑셀 템플릿 다운로드</vs-button>
            <vs-button class="w-full sm:w-auto" color="secondary"  @click="onClickExcelUpload"> 작성 엑셀 업로드 </vs-button>
            <input v-show="false" ref="excelInput" type="file" @change="readFile" accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" />
          </div>
        </div>
      </div>

      <!-- 공정활동분류표 정보 -->
      <div>
        <div class="flex justify-between items-center mt-6 mb-4">
          <h5><i class="dot"></i> 공정활동분류표 정보 </h5>
          <vs-button color="secondary" type="border" @click="onClickCheckRemove">선택삭제</vs-button>
        </div>
        <div style="overflow-x: auto">
          <table>
            <colgroup>
              <col width="60" />
              <col width="60" />
              <col width="120" />
              <col width="120" />
              <col width="120" />
              <col width="120" />
              <col width="120" />
              <col width="120" />
              <col width="120" />
            </colgroup>
            <thead>
            <th><vs-checkbox :value="pagedExcelData.length > 0 && pagedExcelData.every(e => checkList.includes(e.index))" @click.native="checkAll" class="check" /></th>
            <th v-for="th in tableHeader">{{ th }}</th>
            <th>수정/삭제</th>
            </thead>
            <tbody>
            <tr v-for="entry in pagedExcelData" :key="`excelData_${entry.index}`">
              <td><vs-checkbox :vs-value="entry.index" v-model="checkList" class="check" /></td>
              <td v-for="(columnName, columnIndex) in tableHeader.length" :key="`${entry.index}_${entry.item[0]}_${columnIndex}`">
                <div v-if="editExcelIndex === entry.index" class="p-1">
                  <fragment v-if="columnIndex === 0">
                    {{editExcelData.values[columnIndex]}}
                  </fragment>
                  <fragment v-if="columnIndex === 1">
                    <field-selector class="w-full text-left" v-model="editExcelData.values[columnIndex].id" @select="(field) => onSelectField(entry.index, columnIndex, field)"/>
                  </fragment>
                  <fragment v-else-if="columnIndex === 5">
                    <vs-select v-model="editExcelData.values[columnIndex]" class="w-full" autocomplete>
                      <vs-select-item v-for="상태 in ['정상', '비정상', '비상',]" :text="상태" :value="상태" />
                    </vs-select>
                  </fragment>
                  <fragment v-else-if="columnIndex > 0">
                    <ml-input v-model="editExcelData.values[columnIndex]" class="w-full" />
                  </fragment>
                </div>
                <div v-else>
                  <p v-if="columnIndex === 1">{{ entry.item.values[columnIndex].field_name || "-" }}</p>
                  <p v-else>{{ entry.item.values[columnIndex] || "-" }}</p>
                  <p v-if="entry.item.errorMSG" class="errMsg">{{ entry.item.errorMSG[columnIndex] || ""}}</p>
                </div>
              </td>
              <td>
                <div class="flex justify-center gap-3">
                  <!-- 저장/닫기 -->
                  <fragment v-if="editExcelIndex === entry.index">
                    <button @click="onClickFinishExcelData(entry.index)">
                      <feather-icon icon="CheckIcon" svgClasses="h-5 w-5" />
                    </button>
                    <button @click="onClickCancelExcelData(entry.index)">
                      <feather-icon icon="XIcon" svgClasses="h-5 w-5" />
                    </button>
                  </fragment>
                  <!-- 수정/삭제 -->
                  <fragment v-else>
                    <button @click="onClickEditExcelData(entry.index)">
                      <feather-icon icon="Edit2Icon" svgClasses="h-5 w-5" />
                    </button>
                    <button @click="onClickRemoveExcelData(entry.index)">
                      <feather-icon icon="Trash2Icon" svgClasses="h-5 w-5" />
                    </button>
                  </fragment>
                </div>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
        <div class="vs-con-table mt-2">
          <div class="con-pagination-table vs-table--pagination">
            <k-pagination :total="excelLastPage" v-model="excelPage" @change="onChangeExcelPage"></k-pagination>
          </div>
        </div>
      </div>
    </vx-card>
  </div>
</template>

<script>
import FieldSelectModule from "@/views/modules/FieldSelectModule";
import FieldSelector from "@/components/selector/FieldSelector";
import CompanySelector from "@/components/selector/CompanySelector";
import DateSelector from "@/components/selector/DateSelector";
import KPagination from "@/components/kPagination/kPagination.vue";
import moment from "moment";
import HttpRequest from "@/util/HttpRequest";
import ExcelJS from "exceljs";
import excelSupporter from "@/excelSupporter";

export default {
  components: {
    FieldSelectModule,
    FieldSelector,
    CompanySelector,
    DateSelector,
    KPagination,
  },
  props: {
    board: Object,
  },
  data() {
    return {
      tableHeader: ['No.', '사업장명', '팀명', '공장명', '공정명', '상태', '활동명'],
      fields: [],
      stdInfo: {},

      // 엑셀 데이터 관련
      // 데이터구조 :
      //  [
      //    {
      //       values: [1, 'field_name', 'data1', 'data2', ...],
      //       errorMSG: ["", "", "", "", ...]
      //    }
      //  ]
      editExcelIndex: null,
      editExcelData: [],
      excelData: [],
      checkList: [],
      select891: {},

      excelPage: 1,
      excelPerPage: 20,
    };
  },
  computed: {
    projectId() {
      return parseInt(this.$route.params.proj_id);
    },
    boardId() {
      return parseInt(this.$route.params.boardId);
    },
    pageTitle() {
      return this.$store.state.menu.pageTitle;
    },
    pagedExcelData() {
      const start = (this.excelPage - 1) * this.excelPerPage;
      return this.excelData.slice(start, start + this.excelPerPage).map((item, i) => ({
        item,
        index: start + i,
      }));
    },
    excelLastPage() {
      return Math.max(1, Math.ceil(this.excelData.length / this.excelPerPage));
    },
  },
  watch: {
    excelPage() {
      this.editExcelIndex = null;
      this.editExcelData = [];
    },
  },
  async mounted() {
    await this.loadFieldListAll();
  },
  methods: {
    async loadFieldListAll() {
      this.loading(true);
      const loadFieldList = (page) => {
        this.$store.dispatch("project/LOAD_FIELD_LIST", {
          projectId: this.projectId,
          page,
          perPage: 50,
        }).then((response) => {
          const data = response.data;
          this.fields.push(...data)
          if (response.last_page > page) {
            loadFieldList(page + 1);
          } else {
            this.loading(false);
          }
        });
      }
      await loadFieldList(1)
    },

    findFieldByFieldName(fieldName) {
      return this.fields.find((field) => field.field_name === fieldName);
    },

    async onClickDownloadTemplate() {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('공정활동분류표');

      [25, 20, 20, 20, 15, 30].forEach((width, i) => {
        worksheet.getColumn(i + 1).width = width;
      });

      // Row 1: 제목 (업로드 파싱 시 skip)
      excelSupporter.addHeader(worksheet, [
        { value: '공정활동분류표 대량등록', style: 'bg-borderHead', merge: 'A:F' },
      ]);

      // Row 2: 컬럼 헤더 (업로드 파싱 시 skip)
      excelSupporter.addHeader(worksheet, [
        { value: '사업장명', style: 'borderHead', merge: 'A:A' },
        { value: '팀명', style: 'borderHead', merge: 'B:B' },
        { value: '공장명', style: 'borderHead', merge: 'C:C' },
        { value: '공정명', style: 'borderHead', merge: 'D:D' },
        { value: '상태', style: 'borderHead', merge: 'E:E' },
        { value: '활동명', style: 'borderHead', merge: 'F:F' },
      ]);

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = '동국제강_공정활동분류표_대량등록.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    },

    onClickExcelUpload() {
      const excelInput = this.$refs.excelInput;
      excelInput.value = null;
      excelInput.click();
    },
    // 엑셀데이터 파싱
    readFile(event) {
      this.excelData = [];
      this.checkList = [];
      this.excelPage = 1;
      const workbook = new ExcelJS.Workbook();
      const file = event.target.files[0];
      const reader = new FileReader();
      reader.readAsArrayBuffer(file);
      reader.onload = (e) => {
        const buffer = reader.result;
        workbook.xlsx.load(buffer).then((workbook) => {
          workbook.eachSheet((sheet) => {
            sheet.eachRow((row, rowNumber) => {

              // data form 가공
              const cells = row.values.map((cell) => {
                if (cell.richText) return cell.richText.map((rt) => rt.text).join("");
                if (cell instanceof Date) return moment(cell).format("YYYY-MM-DD");
                return cell;
              });
              this.getDataInSheet(sheet, rowNumber, cells)
            });
          });
          //
          // this.excelData = data;
        });
      };
    },

    // rowNumber: 2부터 시작
    // cells: [empty, col1, col2, col3, ...]
    async getDataInSheet(sheet, rowNumber, cells=[]) {
      if (rowNumber > 2) {
        const field = this.findFieldByFieldName(cells[1]);
        const error = [];
        cells[0] = this.excelData.length + 1;
        if (field) {
          cells[1] = {
            id: field.id,
            field_name: field.field_name,
          };
        } else {
          cells[1] = {
            id: 0,
            field_name: cells[1],
          };
          error[1] = "입력된 데이터가 검색되지 않습니다.";
        }

        if (cells[5] !== '정상' && cells[5] !== '비정상' && cells[5] !== '비상') {
          cells[5] = ''
        }
        this.excelData.push({
          values: cells,
          errorMSG: error,
        });
      }
    },


    setMoment(date) {
      let _date = date;
      if (typeof _date === "number") _date = `${_date}`;
      if (typeof _date === "string" && !_date.includes("-")) _date = `${_date.slice(0, 4)}-${_date.slice(4, 6)}-${_date.slice(6, 8)}`;
      return moment(_date).add(1, "days").format("YYYY-MM-DD");
    },
    setPhone(data) {
      let _data = `${data}`;
      if (typeof data === "number" && _data.length === 10 && _data.charAt(0) !== "0") _data = `0${_data}`;
      return _data.replace(/^(\d{3})(\d{4})(\d{4})/, "$1-$2-$3");
    },
    onClickEditExcelData(index) {
      this.editExcelIndex = index;
      this.editExcelData = JSON.parse(JSON.stringify(this.excelData[index]));
    },
    onClickFinishExcelData(index) {
      this.$set(this.excelData, index, this.editExcelData);
      this.onClickCancelExcelData();
    },
    onClickCancelExcelData(index) {
      this.editExcelData = [];
      this.editExcelIndex = null;
    },
    onClickRemoveExcelData(index) {
      this.excelData.splice(index, 1);
    },
    checkAll() {
      const pageIndices = this.pagedExcelData.map(e => e.index);
      const allChecked = pageIndices.length > 0 && pageIndices.every(i => this.checkList.includes(i));
      if (allChecked) {
        this.checkList = this.checkList.filter(i => !pageIndices.includes(i));
      } else {
        const newChecks = pageIndices.filter(i => !this.checkList.includes(i));
        this.checkList = [...this.checkList, ...newChecks];
      }
    },
    onClickCheckRemove() {
      if (this.checkList.length < 1) return;
      this.excelData = this.excelData.filter((_, i) => !this.checkList.includes(i));
      this.excelData.forEach((_, i) => {
        _.values[0] = i + 1;
      });
      this.checkList = [];
      if (this.excelPage > this.excelLastPage) {
        this.excelPage = this.excelLastPage;
      }
    },
    onChangeExcelPage(page) {
      this.excelPage = page;
    },
    onClickSave() {
      // 중복 데이터 제거
      const forms = [...new Set(this.excelData.map((ed) => {
        const data = JSON.parse(JSON.stringify(ed.values))
        if (data[5] !== '정상' && data[5] !== '비정상' && data[5] !== '비상') {
          data[5] = ''
        }
        const form = {
          stdInfo: this.stdInfo,
          사업장: data[1],
          팀명: data[2],
          공장명: data[3],
          공정명: data[4],
          상태: data[5],
          활동명: data[6],
        }
        return JSON.stringify(form)
      }))].map((d) => JSON.parse(d));

      // 엑셀 데이터가 없을때 에러
      if (this.excelData.length < 1) {
        this.customAlert("공정활동분류표 정보 데이터가 없습니다");
        return;
      }

      // 에러가 있는 필드가 있을 경우,
      if (this.excelData.filter((row) => row.errorMSG.filter((msg) => !!msg).length > 0).length > 0) {
        this.customAlert("공정활동분류표 정보에 올바르지 않은 데이터가 있습니다");
        return;
      }

      // 중복 데이터가 있을 경우,
      // if (forms.length !== this.excelData.length) {
      //   this.customAlert("중복된 데이터가 있습니다");
      //   return;
      // }


      this.writeOnClickSaveMultiple({
        posts: forms,
        callback: (result, response) => {
          if (result) {
            const excelData = JSON.parse(JSON.stringify(this.excelData));
            this.excelData = [];
            this.checkList = [];
            let msg = "저장되었습니다";
            if (forms.length != excelData.length) {
              msg += "<br>중복된 데이터는 제거 되었습니다.";
              msg += `<br><br><span style="font-weight: bold">중복된 데이터: ${(excelData.length - forms.length)}건</span>`;
            }
            this.customAlert(msg, "공정활동분류표 엑셀 업로드");
          }
        },
      });
    },
    writeOnClickSaveMultiple(forms) {
      const boardId = this.boardId;
      this.loading(true);
      const date = new Date();
      const header = {...this.header, full: date.format("yyyy-MM-dd")};
      const posts = forms.posts.map((form) => {
        const content = {
          header,
          body: {
            ...form
          },
        };
        return {
          title: this.pageTitle,
          content: JSON.stringify(content),
          projectId: this.projectId,
          company_member_join: true,
        }
      });
      HttpRequest.put(`/api/v1/board/post/${boardId}/multiple`, { posts })
        .then((response) => {
          if (forms.callback) {
            forms.callback(true, response);
          }

          this.loading(false);
        })
        .catch(({ response }) => {
          if (forms.callback) {
            forms.callback(false, response);
          }
          this.loading(false);
        });
    },
    onSelectField (rowIndex, columnIndex, field) {
      this.editExcelData.values[columnIndex] = {
        id: field.id,
        field_name: field.field_name,
      };
      this.editExcelData.errorMSG[columnIndex] = field.id ? "" : "입력된 데이터가 검색되지 않습니다.";
    },

    customAlert(msg, title) {
      const customAlert = new CustomEvent("customAlert", {
        detail: {
          title: !!title ? title : "오류가 발생했습니다",
          message: msg,
        },
      });
      window.dispatchEvent(customAlert);
    },
    loading(open) {
      const loadingDialog = new CustomEvent("loadingDialog", {
        detail: {
          open,
        },
      });
      window.dispatchEvent(loadingDialog);
    },
  },
};
</script>

<style scoped lang="scss">
table {
  table-layout: fixed;
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    text-align: center;
    padding: 0;
  }

  th {
    font-weight: 700;
    font-size: 14px;
    line-height: 17px;

    background: #fafafa;
    border-top: 1px solid #cdcdcd;
    border-bottom: 1px solid #cdcdcd;
    padding: 16px 0;
  }

  td {
    font-weight: 400;
    font-size: 14px;
    line-height: 17px;

    border-bottom: 1px solid #cdcdcd;
    padding: 16px 0;
  }

  button {
    width: 32px;
    height: 32px;

    display: flex;
    align-items: center;
    justify-content: center;

    color: #7e72f2;
    background: none;
    border: 1px solid #7e72f2;
    border-radius: 4px;
    cursor: pointer;

    span {
      margin: 0;
    }
  }

  .check {
    justify-content: center !important;

    ::v-deep {
      span {
        margin: 0;
      }
    }
  }
}

.errMsg {
  font-weight: 400;
  font-size: 12px;
  line-height: 17px;
  text-align: center;
  color: #ea5455;
  margin-top: 2px;
}
</style>
